import React, { useEffect, useState, SyntheticEvent } from 'react'

import { TextInputWrapper } from '../../pp-react-wrappers'

import {
  DropdownMenu,
  Button,
  ContextualAlert,
  TextArea,
  RadioButton,
} from '@paypalcorp/pp-react'

import type {
  MultipartyFormProps,
  CountriesType,
  FormDataType,
  FPTITypes,
} from './MultipartyForm.types'

import formContent from './multiparty-form.yaml'
import { getRequest } from '../../../utils/request'
import styles from './multiparty-form.module.css'
import withAnalytics from '../../../utils/withAnalytics'
import { downloadZipFile } from '../../../utils'
import GreenTick from '../../icons/greenTick'
import { removeGAParam } from '../../pp-react-wrappers'

const pardotFormurl = 'https://pypd.paypal-mktg.com/l/925803/2022-12-07/2qjkq'
const pardotFormName = 'NA-US-FRH-22-Partner-Self-Service-Not-Live'
const emailAddressRegex = /.+\@.+\..+/
const companyWebsiteRegex =
  /^((https?|ftp|smtp):\/\/)?(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]+(\/[a-zA-Z0-9#-]+\/?)*$/

const MultipartyForm: React.FC<MultipartyFormProps> = ({
  fpti,
  auto = false,
}) => {
  const [formSubmitted, setFormSubmitted] = useState(false)
  const [countries, setCountries] = useState<CountriesType[]>([])
  const [cav_lt_10m, setCAVFlag] = useState(false)

  const fptiData: FPTITypes | boolean =
    typeof window !== `undefined` && window.fpti

  const [formData, setFormData] = useState<FormDataType>({
    firstName: '',
    lastName: '',
    emailAddress: '',
    company: '',
    companyWebsite: '',
    country: '',
    describeYourSolution: '',
    companyType: '',
    clientOnlineVolume: '',
    productsandServices: 'yes',
    cardProcessing: 'yes',
    currentlyProcessing: '',
    privacyConsent: false,
  })

  const [formErrors, setFormErrors] = useState({
    firstName: { isValid: false, isTouched: false },
    lastName: { isValid: false, isTouched: false },
    emailAddress: { isValid: false, isTouched: false },
    company: { isValid: false, isTouched: false },
    companyWebsite: { isValid: false, isTouched: false },
    country: { isValid: false, isTouched: false },
    describeYourSolution: { isValid: false, isTouched: false },
    companyType: { isValid: false, isTouched: false },
    clientOnlineVolume: { isValid: false, isTouched: false },
    privacyConsent: { isValid: false, isTouched: false },
    currentlyProcessing: { isValid: false, isTouched: false },
  })

  const lessThanTargetVl = '1000000'
  const greaterThanTargetVl = '10000000'

  const getCountries = async () => {
    try {
      const { data } = await getRequest('/apis/countries')
      setCountries(
        data.countries.map((c) => {
          return { value: c.countryCode, primaryText: c.countryName }
        }),
      )
    } catch (err) {
      const countries = [{ value: 'US', primaryText: 'United States' }]
      setCountries(countries)
    }
  }

  const onFormChange = (field: string, value: any) => {
    if (field === 'clientOnlineVolume') {
      localStorage.setItem('client-volume', value)
    }

    setFormData((prevFormData) => ({ ...prevFormData, [field]: value }))

    if (!value) {
      setFormErrors((prevFormErrors) => ({
        ...prevFormErrors,
        [field]: { isTouched: true, isValid: false },
      }))
    } else {
      // eslint-disable-next-line no-lonely-if
      if (
        (field === 'emailAddress' && !emailAddressRegex.test(value)) ||
        (field === 'companyWebsite' && !companyWebsiteRegex.test(value))
      ) {
        setFormErrors((prevFormErrors) => ({
          ...prevFormErrors,
          [field]: { isTouched: true, isValid: false },
        }))
      } else if (
        field === 'clientOnlineVolume' &&
        value === greaterThanTargetVl
      ) {
        setFormErrors((prevFormErrors) => ({
          ...prevFormErrors,
          clientOnlineVolume: { isTouched: true, isValid: true },
          currentlyProcessing: { isTouched: true, isValid: true },
        }))
      } else if (
        field === 'clientOnlineVolume' &&
        (value === lessThanTargetVl || value === '')
      ) {
        setFormErrors((prevFormErrors) => ({
          ...prevFormErrors,
          clientOnlineVolume: { isTouched: true, isValid: true },
          currentlyProcessing: { isTouched: true, isValid: false },
        }))
      } else {
        setFormErrors((prevFormErrors) => ({
          ...prevFormErrors,
          [field]: { isTouched: true, isValid: true },
        }))
      }
    }
  }

  const validate = (type: any) => {
    const errors = {
      ...formErrors,
      currentlyProcessing: {
        ...formErrors.currentlyProcessing,
        isValid:
          formData.cardProcessing === 'no'
            ? true
            : formErrors.currentlyProcessing.isValid,
      },
    }

    return Object.values(errors).reduce((cur, next) => {
      return (cur = cur && next[type])
    }, true)
  }

  const constantFptiValues = {
    page: pardotFormName,
    pgrp: pardotFormName,
    component: 'devdiscoverynodeweb',
    space_key: 'SKEXJE',
    page_name: fptiData && fptiData.page.replace(/:/g, '_'),
  }

  const handleFormSubmit = (pdFormName: string, e: SyntheticEvent) => {
    e.preventDefault()

    // Validate Form
    const isFormValid = validate('isTouched') && validate('isValid')

    if (!isFormValid) {
      Object.keys(formData).forEach((field) => {
        if (
          field === 'currentlyProcessing' &&
          formData.clientOnlineVolume === greaterThanTargetVl
        ) {
          setFormErrors((prevFormErrors) => ({
            ...prevFormErrors,
            currentlyProcessing: { isTouched: true, isValid: true },
          }))
        } else if (
          (field === 'privacyConsent' && !formData[field]) ||
          !formData[field]
        ) {
          setFormErrors((prevFormErrors) => ({
            ...prevFormErrors,
            [field]: { isTouched: true, isValid: false },
          }))
        }
      })
    }

    if (isFormValid && fptiData) {
      const options = {
        data: {
          ...constantFptiValues,
          e: 'cl',
          link: `Form submitted button clicked`,
          event_name:
            fptiData &&
            `${fptiData.page.replace(/:/g, '_')}_form_submit_button_pressed`,
        },
      }

      if (window.PAYPAL?.analytics?.instance) {
        window.PAYPAL?.analytics?.instance?.record(options)
      }

      const pardotFormEle = document.getElementById(
        pdFormName,
      ) as HTMLFormElement
      pardotFormEle.submit()
    }
  }

  const downloadZip = () => {
    downloadZipFile()

    if (fptiData) {
      const options = {
        data: {
          ...constantFptiValues,
          e: 'cl',
          link: `docs_multiparty - download zip file`,
          event_name:
            fptiData &&
            `${fptiData.page.replace(
              /:/g,
              '_',
            )}_download_zip_file_button_pressed`,
        },
      }

      if (window.PAYPAL?.analytics?.instance) {
        window.PAYPAL?.analytics?.instance?.record(options)
      }
    }
  }

  useEffect(() => {
    ;(async function () {
      await getCountries()
    })()

    const isFormSubmitted =
      typeof window !== `undefined` &&
      window.location.search !== '' &&
      window.location?.search.substring(1).split('=')[1]
    setFormSubmitted(!!isFormSubmitted)

    // Success Path
    if (!!isFormSubmitted && fptiData) {
      const options = {
        data: {
          ...constantFptiValues,
          e: 'im',
          link: `Multiparty form successfully submitted`,
          event_name:
            fptiData &&
            `${fptiData.page.replace(/:/g, '_')}_form_submission_success`,
        },
      }

      if (window.PAYPAL?.analytics?.instance) {
        window.PAYPAL?.analytics?.instance?.record(options)
      }
    }

    const clientVolume = localStorage.getItem('client-volume')
    if (clientVolume && clientVolume === lessThanTargetVl) {
      setCAVFlag(true)
    } else {
      setCAVFlag(false)
    }
  }, [])

  return (
    <div className={styles.successCardmb2}>
      {formSubmitted && cav_lt_10m && (
        <div className={styles.block}>
          <h2>We recommend</h2>
          <p className={styles.successMessage}>
            <span className={styles.greenTickSpace}>
              <GreenTick />
            </span>
            A PayPal representative will reach out to you once we evaluate your
            business needs.
          </p>
          <div data-testid="form-submit-success" className={styles.container}>
            <div className={styles.header}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="96"
                height="96"
                viewBox="0 0 96 96"
                fill="none"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M51.2694 13.696C49.6764 11.4346 46.3236 11.4347 44.7306 13.6961L42.3616 17.0589C41.2489 18.6385 39.1659 19.1967 37.4124 18.385L33.6794 16.6572C31.1691 15.4953 28.2655 17.1717 28.0166 19.9266L27.6465 24.0234C27.4726 25.9478 25.9478 27.4726 24.0234 27.6465L19.9266 28.0166C17.1717 28.2655 15.4953 31.1691 16.6572 33.6794L18.385 37.4124C19.1967 39.1659 18.6385 41.2489 17.0589 42.3616L13.696 44.7306C11.4346 46.3236 11.4347 49.6764 13.6961 51.2694L17.0589 53.6384C18.6385 54.7511 19.1967 56.8341 18.385 58.5876L16.6572 62.3206C15.4953 64.8309 17.1717 67.7345 19.9266 67.9834L24.0234 68.3535C25.9478 68.5274 27.4726 70.0522 27.6465 71.9766L28.0166 76.0734C28.2655 78.8283 31.1691 80.5047 33.6794 79.3428L37.4124 77.615C39.1659 76.8033 41.2489 77.3615 42.3616 78.9411L44.7306 82.3039C46.3236 84.5653 49.6764 84.5653 51.2694 82.3039L53.6384 78.9411C54.7511 77.3615 56.8341 76.8033 58.5876 77.615L62.3206 79.3428C64.8309 80.5047 67.7345 78.8283 67.9834 76.0734L68.3535 71.9766C68.5274 70.0522 70.0522 68.5274 71.9766 68.3535L76.0734 67.9834C78.8283 67.7345 80.5047 64.8309 79.3428 62.3206L77.615 58.5876C76.8033 56.8341 77.3615 54.7511 78.9411 53.6384L82.3039 51.2694C84.5653 49.6764 84.5653 46.3236 82.3039 44.7306L78.9411 42.3616C77.3615 41.2489 76.8033 39.1659 77.615 37.4124L79.3428 33.6794C80.5047 31.1691 78.8283 28.2655 76.0734 28.0166L71.9766 27.6465C70.0522 27.4726 68.5274 25.9478 68.3535 24.0234L67.9834 19.9266C67.7345 17.1717 64.8309 15.4953 62.3206 16.6572L58.5876 18.385C56.8341 19.1967 54.7511 18.6385 53.6384 17.0589L51.2694 13.696ZM57.8763 34.8326L44.128 52.7494L38.1583 46.6185C37.0026 45.4317 35.1037 45.4064 33.9169 46.562C32.73 47.7176 32.7048 49.6166 33.8604 50.8034L41.4423 58.59C43.1427 60.3363 45.9965 60.1684 47.4803 58.2346L62.6354 38.4844C63.6439 37.1702 63.396 35.2874 62.0818 34.279C60.7676 33.2705 58.8848 33.5184 57.8763 34.8326Z"
                  fill="#6FC400"
                />
              </svg>

              <h2>Expanded Checkout solution</h2>
            </div>
            <p>
              These are the steps to integrate Expanded Checkout on your
              platform or marketplace:
            </p>
            <ol>
              <li>
                <a className="linkFormat" href="/docs/multiparty/get-started/">
                  Get started
                </a>
              </li>
              <li>
                <a className="linkFormat" href="#">
                  Configure your accounts
                </a>
              </li>
              <li>
                <a
                  className="linkFormat"
                  href="/docs/multiparty/seller-onboarding/"
                >
                  Onboard sellers
                </a>
              </li>
              <li>
                <a
                  className="linkFormat"
                  href="/docs/multiparty/checkout/advanced/integrate/"
                >
                  Expanded Checkout
                </a>
              </li>
              <li>
                <a
                  className="linkFormat"
                  href="/docs/multiparty/integration-checklist"
                >
                  Integration checklist
                </a>
              </li>
            </ol>
          </div>
        </div>
      )}

      {formSubmitted && !cav_lt_10m && (
        <div className={styles.block}>
          <h2>Thank you</h2>
          <p className={styles.successMessage}>
            <span className={styles.greenTickSpace}>
              <GreenTick />
            </span>
            We are reviewing your request. A PayPal representative will contact
            you as soon as possible.
          </p>
        </div>
      )}

      {!formSubmitted && (
        <>
          <h1>Tell us about your platform</h1>
          <p>
            Complete this form, and a PayPal representative will contact you
            after they evaluate your business needs.
          </p>
          <ContextualAlert type="info" className={styles.mpInfo}>
            <span className={styles.mpInfoContent}>
              If you are a{' '}
              <span className={styles.mpBoldText}>
                seller or small business proprietor
              </span>{' '}
              interested in PayPal solutions, do not complete this form.
              Instead, visit{' '}
              <a
                href="https://www.paypal.com/us/business"
                className={`${styles.mpLink} ${styles.mpBoldText}`}
              >
                PayPal for Business
              </a>{' '}
              to learn more about the latest features and benefits of a PayPal
              Business Account.{' '}
            </span>
          </ContextualAlert>

          {!auto && (
            <form
              method="POST"
              action={pardotFormurl}
              onSubmit={(e: SyntheticEvent) =>
                handleFormSubmit(pardotFormName, e)
              }
              id={pardotFormName}
              data-pardot-form-name={pardotFormName}
            >
              <div className="ppvx_container--form">
                <div className={`ppvx_col-form--half ${styles.mppl0}`}>
                  <TextInputWrapper
                    nameValue="firstName"
                    errorPresent={
                      formErrors.firstName.isTouched &&
                      (formErrors.firstName.isValid ? false : true)
                    }
                    errorTextForFpti={formContent.content.firstname_error}
                    name="firstName"
                    label={formContent.content.firstname_label}
                    value={formData.firstName}
                    onInput={(e: any) => {
                      onFormChange(e.target.name, e.target.value)
                    }}
                    helperText="​"
                    required
                    errorText={
                      formErrors.firstName.isTouched &&
                      !formErrors.firstName.isValid
                        ? formContent.content.firstname_error
                        : ''
                    }
                  />

                  <TextInputWrapper
                    nameValue="lastName"
                    errorPresent={
                      formErrors.lastName.isTouched &&
                      (formErrors.lastName.isValid ? false : true)
                    }
                    errorTextForFpti={formContent.content.lastname_error}
                    name="lastName"
                    label={formContent.content.lastname_label}
                    value={formData.lastName}
                    onInput={(e: any) => {
                      onFormChange(e.target.name, e.target.value)
                    }}
                    helperText="​"
                    required
                    errorText={
                      formErrors.lastName.isTouched &&
                      !formErrors.lastName.isValid
                        ? formContent.content.lastname_error
                        : ''
                    }
                  />

                  <TextInputWrapper
                    nameValue="emailAddress"
                    errorPresent={
                      formErrors.emailAddress.isTouched &&
                      (formErrors.emailAddress.isValid ? false : true)
                    }
                    errorTextForFpti={formContent.content.emailaddress_error}
                    name="emailAddress"
                    label={formContent.content.emailaddress_label}
                    value={formData.emailAddress}
                    onInput={(e: any) => {
                      onFormChange(e.target.name, e.target.value)
                    }}
                    helperText="​"
                    required
                    errorText={
                      formErrors.emailAddress.isTouched &&
                      !formErrors.emailAddress.isValid
                        ? formContent.content.emailaddress_error
                        : ''
                    }
                  />

                  <TextInputWrapper
                    nameValue="company"
                    errorPresent={
                      formErrors.company.isTouched &&
                      (formErrors.company.isValid ? false : true)
                    }
                    errorTextForFpti={formContent.content.company_error}
                    name="company"
                    label={formContent.content.company_label}
                    value={formData.company}
                    onInput={(e: any) => {
                      onFormChange(e.target.name, e.target.value)
                    }}
                    helperText="​"
                    required
                    errorText={
                      formErrors.company.isTouched &&
                      !formErrors.company.isValid
                        ? formContent.content.company_error
                        : ''
                    }
                  />
                  <TextInputWrapper
                    nameValue="companyWebsite"
                    errorPresent={
                      formErrors.companyWebsite.isTouched &&
                      (formErrors.companyWebsite.isValid ? false : true)
                    }
                    errorTextForFpti={formContent.content.website_error}
                    name="companyWebsite"
                    label={formContent.content.website_label}
                    value={formData.companyWebsite}
                    onInput={(e: any) => {
                      onFormChange(e.target.name, e.target.value)
                    }}
                    helperText="​"
                    required
                    errorText={
                      formErrors.companyWebsite.isTouched &&
                      !formErrors.companyWebsite.isValid
                        ? formContent.content.website_error
                        : ''
                    }
                  />

                  <DropdownMenu
                    name="country"
                    label={formContent.content.country_label}
                    value={formData.country}
                    options={countries}
                    onChange={(e) => {
                      onFormChange('country', e.target.value)
                    }}
                    helperText="​"
                    errorText={
                      formErrors.country.isTouched &&
                      !formErrors.country.isValid
                        ? formContent.content.country_error
                        : ''
                    }
                  />
                  <TextArea
                    name="describeYourSolution"
                    className={styles.mpText}
                    label={formContent.content.description_label}
                    value={formData.describeYourSolution}
                    required
                    onChange={(e) =>
                      onFormChange(e.target.name, e.target.value)
                    }
                    errorText={
                      formErrors.describeYourSolution.isTouched &&
                      !formErrors.describeYourSolution.isValid
                        ? formContent.content.description_error
                        : ''
                    }
                  />
                  <DropdownMenu
                    name="companyType"
                    label={formContent.content.companyType_label}
                    value={formData.companyType}
                    options={[
                      {
                        value: 'Platform',
                        primaryText: 'Platform',
                      },
                      {
                        value: 'Marketplace',
                        primaryText: 'Marketplace',
                      },
                      {
                        value: 'Crowdfunding',
                        primaryText: 'Crowdfunding',
                      },
                      {
                        value: 'Sharing/Gig Economy',
                        primaryText: 'Sharing/Gig Economy',
                      },
                      {
                        value: 'Systems Integrator',
                        primaryText: 'Systems Integrator',
                      },
                      {
                        value: 'Developer',
                        primaryText: 'Developer',
                      },
                      {
                        value: 'Direct Seller',
                        primaryText: 'Direct Seller',
                      },
                    ]}
                    onChange={(e) => {
                      onFormChange('companyType', e.target.value)
                    }}
                    helperText="​"
                    errorText={
                      formErrors.companyType.isTouched &&
                      !formErrors.companyType.isValid
                        ? formContent.content.companyType_error
                        : ''
                    }
                  />
                  <DropdownMenu
                    name="clientOnlineVolume"
                    label={formContent.content.clientAnnual_label}
                    value={formData.clientOnlineVolume}
                    options={[
                      {
                        value: lessThanTargetVl,
                        primaryText: 'Less than $10 Million',
                      },
                      {
                        value: greaterThanTargetVl,
                        primaryText: 'More than $10 Million',
                      },
                    ]}
                    onChange={(e) => {
                      onFormChange('clientOnlineVolume', e.target.value)
                    }}
                    helperText="​"
                    errorText={
                      formErrors.clientOnlineVolume.isTouched &&
                      !formErrors.clientOnlineVolume.isValid
                        ? formContent.content.clientAnnual_error
                        : ''
                    }
                  />
                </div>

                {formData.clientOnlineVolume === lessThanTargetVl ? (
                  <div className={`ppvx_col-form--half ${styles.mppl0}`}>
                    <RadioButton
                      name="productsandServices"
                      label="Are your sellers the one who are going to receive the Payments for products/services?"
                      value={formData.productsandServices}
                      options={[
                        { label: 'Yes', value: 'yes' },
                        { label: 'No', value: 'no' },
                      ]}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onFormChange(e.target.name, e.target.value)
                      }
                    />
                    <RadioButton
                      name="cardProcessing"
                      label="Do you have card processing relationships in place?"
                      value={formData.cardProcessing}
                      className={styles.mpmt}
                      options={[
                        { label: 'Yes', value: 'yes' },
                        { label: 'No', value: 'no' },
                      ]}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        onFormChange(e.target.name, e.target.value)
                      }
                    />
                    {formData.cardProcessing === 'yes' && (
                      <div className={styles.mpmt}>
                        <div className={styles.paymentProcessorLabel}>
                          Who are you currently processing with?
                        </div>
                        <TextInputWrapper
                          nameValue="currentlyProcessing"
                          errorPresent={
                            formErrors.currentlyProcessing.isTouched &&
                            (formErrors.currentlyProcessing.isValid
                              ? false
                              : true)
                          }
                          errorTextForFpti={
                            formContent.content.payment_processor_error
                          }
                          name="currentlyProcessing"
                          label={formContent.content.payment_processor}
                          value={formData.currentlyProcessing}
                          onChange={(e) => {
                            onFormChange(e.target.name, e.target.value)
                          }}
                          helperText="​"
                          required
                          errorText={
                            formErrors.currentlyProcessing.isTouched &&
                            !formErrors.currentlyProcessing.isValid
                              ? formContent.content.payment_processor_error
                              : ''
                          }
                        />
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="ppvx_col-form--half">
                  <div className={styles.privacyTermContainer}>
                    <div className={styles.privacyTerm}>
                      <span>
                        <input
                          type="checkbox"
                          name="privacyConsent"
                          checked={formData.privacyConsent}
                          style={{
                            width: '1.5rem',
                            height: '1.5rem',
                            borderRadius: '10px',
                          }}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            onFormChange(e.target.name, e.target.checked)
                          }}
                          required
                        />
                      </span>
                      <span>
                        I have read and acknowledged the{' '}
                        <a
                          href="https://www.paypal.com/webapps/mpp/ua/privacy-full"
                          className="terms_and_conditions_link dx-external-href"
                          target="_blank"
                          rel="noreferrer"
                        >
                          Privacy Policy
                        </a>{' '}
                        and agree that PayPal may contact me at the email above
                        to obtain more information about my submission.
                      </span>
                    </div>
                    {formErrors.privacyConsent.isTouched &&
                      !formErrors.privacyConsent.isValid && (
                        <div className={styles.privacyTermError}>
                          <span>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="#C50A0C"
                              viewBox="0 0 24 24"
                              width="1.65rem"
                              height="1.65rem"
                              data-ppui="true"
                            >
                              <path
                                fillRule="evenodd"
                                d="M11.105 3.554a1 1 0 0 1 1.79 0l7.498 14.998a1 1 0 0 1-.895 1.447H4.502a1 1 0 0 1-.895-1.447l7.498-14.998zm.384 10.96h1.02c.168 0 .276-.096.288-.264.018-.428.042-.85.067-1.28.055-.98.113-2.002.113-3.244v-.948c0-.18-.096-.276-.276-.276h-1.404c-.18 0-.277.096-.277.276v.948c0 1.242.058 2.264.114 3.243.024.43.048.853.066 1.281.013.168.12.264.289.264zm.51 3.486A1 1 0 1 0 12 16a1 1 0 0 0 0 2z"
                                clipRule="evenodd"
                                data-ppui="true"
                              />
                            </svg>
                          </span>
                          <span>
                            Please accept terms and conditions to continue
                            signing up.
                          </span>
                        </div>
                      )}
                  </div>
                </div>

                <div className="ppvx_row">
                  <div className="ppvx_col-form--full signup-form">
                    <Button type="submit" style={{ width: '424px' }}>
                      {formContent.content.signupbutton_label}
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </>
      )}
    </div>
  )
}

export default withAnalytics(MultipartyForm)
